<template>
  <div class="sidenav-content" :class="{ 'collapsed': isCollapsed }">
    <div class="navbar-collapse w-auto h-100" id="sidenav-collapse-main">
      <ul class="navbar-nav">
        <li
            @click="handleMenuClick(menu)"
            v-for="(menu, key) in menuOptions"
            :key="key"
            class="nav-item"
            :class="{ 'collapsed-item': isCollapsed }"
        >
          <SidenavItem
              :url="menu.routeName"
              :class="isActiveMenu(menu)"
              :disabled="menu.disabled"
              :is-collapsed="isCollapsed"
              :label="menu.label"
          >
            <template v-slot:icon>
              <img :src="menu.icon" alt="menuIcon" class="menu-icon" />
            </template>
            <template v-slot:text>
              <span class="menu-text">
                {{ menu.label }}
              </span>
            </template>
          </SidenavItem>
        </li>
      </ul>

      <!-- Event Banner Section -->
      <div class="event-banner-section" v-if="!isCollapsed">
        <slot name="event-banner">
          <!-- Default Event Banner -->
          <div class="event-card">
            <div class="event-header">
              <div class="event-icon">
                <i class="fas fa-calendar-alt"></i>
              </div>
              <div class="event-badge">UPCOMING</div>
            </div>
            <h3 class="event-title">Critical Materials Conference</h3>
            <p class="event-subtitle">EV & BATTERY '26</p>
            <div class="event-details">
              <div class="event-detail-item">
                <i class="fas fa-map-marker-alt"></i>
                <span>Berlin, Germany</span>
              </div>
              <div class="event-detail-item">
                <i class="fas fa-calendar"></i>
                <span>24-25 February 2026</span>
              </div>
            </div>
            <button class="event-btn">Learn More</button>
          </div>
        </slot>
      </div>

      <!-- Collapsed Event Banner -->
      <div class="event-banner-collapsed" v-else>
        <div class="event-icon-collapsed">
          <i class="fas fa-calendar-alt"></i>
        </div>
      </div>
    </div>
  </div>

  <!-- Drawer for forms -->
  <NDrawer v-model:show="showForm" :height="drawerHeight" :width="380" placement="right" :style="{ top: drawerTop }">
    <NDrawerContent>
      <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h3 class="form-title">{{ title }}</h3>
            </div>
            <div class="container p-0">
              <div class="contact-title">
                <p>{{ message }}</p>
              </div>
              <div>
                <div class="user-details">
                  <div class="col-12">
                    <h6>Your Details:</h6>
                  </div>
                  <div class="col-12">
                    <table class="details-table">
                      <tbody>
                        <tr>
                          <td><span>Name: </span></td>
                          <td><span>{{ SUBSCRIPTION_INFO?.fullname }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="form-section">
                  <div class="col-sm-12 mt-2">
                    <div class="form-group">
                      <div class="phone-group">
                        <NSelect
                            v-model:value="input.contactNumberPrefix"
                            :options="mobileList"
                            filterable
                            placeholder="Select Country"
                            class="phone-select"
                            :theme-overrides="selectTheme"
                            @update:value="handleUpdateContactNumber"
                        />
                        <input
                            v-model="input.contactNumber"
                            type="text"
                            class="phone-input"
                            placeholder="Contact number"
                            required
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-12">
                    <div class="form-group">
                          <textarea
                              id="form_message"
                              v-model="input.message"
                              name="message"
                              class="form-control message-form custom-form"
                              placeholder="*Your message"
                              rows="6"
                              required="required"
                              data-error="Please,leave us a message."
                          />
                      <div class="help-block with-errors" />
                    </div>
                  </div>
                </div>

                <div class="form-actions">
                  <div class="col-md-12 btn-send">
                    <button class="btn btn-primary" @click="sendToMarketing">
                      Send message
                    </button>
                  </div>
                  <div class="col-sm-12">
                    <p class="required pt-3">* These fields are required.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </NDrawerContent>
  </NDrawer>
</template>

<script setup>
import { useRoute, useRouter } from "vue-router";
import { ROUTES } from "../../routes/names";
import SidenavItem from "./SidenavItem.vue";
import { useI18n } from "vue-i18n";
import { ref, reactive, watch, onMounted, onBeforeUnmount, defineProps, computed } from "vue";
import {
  NDrawer,
  NDrawerContent,
  NSelect
} from "naive-ui";
import { getSubscriptionInfo, SUBSCRIPTION_INFO } from "../../composables/subscription";
import { sendInquiry, RESPONSE, LEAD_TYPE } from "../../composables/marketSupport/inquiry";
import { COUNTRIES_LIST } from "../../utils/generic.ts";
const selectTheme = {
  peers: {
    InternalSelection: {
      borderHover: '1px solid #213ce5',
      borderFocus: '1px solid #213ce5',
      borderActive: '1px solid #213ce5',
      boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowActive: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowHover: 'none',
      caretColor: '#213ce5',
      arrowColorActive: '#213ce5',
      arrowColorHover: '#213ce5',
      color: '#f0f2f5',
      colorActive: '#ffffff',
      colorFocus: '#ffffff',
    },
    InternalSelectMenu: {
      optionColorActive: 'rgba(33, 60, 229, 0.08)',
      optionColorActivePending: 'rgba(33, 60, 229, 0.12)',
      optionColorPending: 'rgba(33, 60, 229, 0.05)',
      optionTextColorActive: '#213ce5',
      optionTextColorChildActive: '#213ce5',
      optionCheckColor: '#213ce5',
      actionDividerColor: '#213ce5',
      loadingColor: '#213ce5',
      scrollbarColor: 'rgba(33, 60, 229, 0.3)',
      scrollbarColorHover: 'rgba(33, 60, 229, 0.5)',
    }
  }
}
const props = defineProps({
  isCollapsed: {
    type: Boolean,
    default: false
  }
});
// Navbar shrinks from 70px to 60px below 767.98px; keep the drawer's offset in sync
const navbarHeight = ref(window.innerWidth <= 767.98 ? 60 : 70);
const updateNavbarHeight = () => {
  navbarHeight.value = window.innerWidth <= 767.98 ? 60 : 70;
};
onMounted(() => window.addEventListener("resize", updateNavbarHeight));
onBeforeUnmount(() => window.removeEventListener("resize", updateNavbarHeight));

const drawerHeight = computed(() => `calc(100vh - ${navbarHeight.value}px)`);
const drawerTop = computed(() => `${navbarHeight.value}px`);

const mobileList = COUNTRIES_LIST.map((nth) => {
  return {
    label: `(${nth.mobileCode}) ${nth.name}`,
    value: nth.code,
    mobile: nth.mobileCode,
  };
});

const type = ref("");
const input = reactive({
  firstname: null,
  lastname: null,
  company: null,
  country: null,
  email: null,
  contactNumberPrefix: null,
  contactNumber: null,
  interests: null,
  sectors: null,
  message: null,
  leadType: LEAD_TYPE.value?.INQUIRY,
  product: null,
  description: null,
  subExpiry: null,
  isMultiple: false,
  parentSubscriptionPackages: null
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const showForm = ref(false);
const title = ref("");
const message = ref("");
const menuOptions = ref([]);

const buildMenu = () => {
  menuOptions.value = [
    {
      label: t("App.sidebarMenu.dashboardPage"),
      routeName: ROUTES.DASHBOARD.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Home.png",
      disabled: false,
    },
    {
      label: t("App.sidebarMenu.subscriptionsPage"),
      routeName: ROUTES.FILES.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Market_Analysis.png",
      disabled: false,
    },
    {
      label: t("App.sidebarMenu.proximaPage"),
      routeName: ROUTES.PROXIMA_PROFILES.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Asset_Profiles.png",
      disabled: !SUBSCRIPTION_INFO?.hasAssets,
    },
    {
      label: t("App.sidebarMenu.costAnalysis"),
      routeName: ROUTES.COST_ANALYSIS.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Cost_Analysis.png",
      disabled: false,
    },
    {
      label: t("App.sidebarMenu.marketData"),
      routeName: ROUTES.MARKET_DATA.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Data_Dashboards.png",
      disabled: !SUBSCRIPTION_INFO?.hasDashboards,
    },
    {
      label: t("App.sidebarMenu.supplyChainMonthlies"),
      routeName: ROUTES.MONTHLIES.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Supply_Chain_Monthlies.png",
      disabled: false,
    },
  ];

  if (SUBSCRIPTION_INFO?.pricingServicesElements?.length > 0) {
    menuOptions.value.push({
      label: t("App.sidebarMenu.pricingServices"),
      routeName: ROUTES.PRICING_SERVICES.name,
      icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Analyst_Access.png",
      disabled: false,
    });
  }

  menuOptions.value.push({
    label: t("App.sidebarMenu.needHelpTitle"),
    routeName: ROUTES.SUPPORT.name,
    icon: "https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Analyst_Access.png",
    disabled: false,
  });
};
watch(
    () => SUBSCRIPTION_INFO, () => {
      buildMenu();
    },
    { immediate: true, deep: true }
);

onMounted(() => {
  getSubscriptionInfo(false);
});

const sendToMarketing = async () => {
  let isReady = input.contactNumberPrefix !== null &&
      input.contactNumberPrefix !== "" &&
      input.contactNumber !== null &&
      input.contactNumber !== "" &&
      input.message !== null &&
      input.message !== "";

  if (isReady) {
    await sendInquiry(input, type.value);
    let res = RESPONSE.value;
    if (res === 200) {
      showForm.value = false;
      window.$message.success("Message sent successfully!");
    } else {
      window.$message.error("Request failed.");
    }
  } else {
    window.$message.error('Please complete all the forms before submitting');
  }
};

const handleUpdateContactNumber = (value, opt) => {
  input.contactNumberPrefix = `${opt.value} (${opt.mobile})`;
  input.country = countriesList
      .filter((country) => country.value === opt.value)
      .map((c) => c.label)[0];
};

const countriesList = COUNTRIES_LIST.map((nth) => {
  return { label: nth.name, value: nth.code, mobile: nth.mobileCode };
});

const handleMenuClick = (menu) => {
  if (menu.disabled) {
    // Open contact form so the user can request access
    showForm.value = true;
    type.value = menu.label;
    title.value = menu.label;
    message.value = `You are not subscribed to ${menu.label} - please provide your details below and a member of our team will get in touch.`;
    return;
  }
  else {
    router.push({ name: menu.routeName });
  }
};

const isActiveMenu = (menu) => {
  if (route.name === menu.routeName) {
    return "active";
  }
  if (menu.disabled) {
    return "disabled-menu";
  }
  return "";
};
</script>

<style lang="scss" scoped>
.card-header {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  margin: 0;
  padding: 0.5rem;
  background-color: #f8f9fa;
  flex-shrink: 0;
  .form-title {
    font-size: 1rem;
    margin-bottom: 0;
    margin-top: 0.25rem;
  }
}
.sidenav-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;

  &.collapsed {
    .navbar-nav {
      padding: 0.5rem 0;
    }
  }
}

.navbar-collapse {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.navbar-nav {
  padding: 0.5rem;
  width: 100%;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-item {
  margin-top: 0 !important;
  flex-shrink: 0;

  &.collapsed-item {
    text-align: center;
  }
}

.menu-icon {
  width: 20px;
  height: 20px;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-item:hover .menu-icon {
  transform: scale(1.08);
}

.menu-text {
  color: white;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.3;
}

.badge-coming-soon {
  background: linear-gradient(135deg, #F2AB26 0%, #F59E0B 100%);
  color: white;
  font-size: 0.55rem;
  padding: 0.2rem 0.4rem;
  border-radius: 0 !important;
  margin-left: 0.5rem;
  display: inline-block;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.disabled-menu {
  .menu-text {
    color: rgba(255, 255, 255, 0.4) !important;
  }

  .menu-icon {
    opacity: 0.4;
  }

  &:hover {
    cursor: not-allowed;
  }
}

/* Event Banner Section */
.event-banner-section {
  margin-top: auto;
  //text-align: center;
  padding:0 0.75rem 0 0.75rem;
  flex-shrink: 0;
}

.event-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(10px);
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.event-icon {
  width: 34px;
  height: 34px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.event-badge {
  background: #10B981;
  color: white;
  font-size: 0.6rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0 !important;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.event-title {
  color: white;
  font-size: 0.9375rem;
  font-weight: 700;
  margin: 0 0 0.2rem 0;
  line-height: 1.3;
}

.event-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
}

.event-details {
  margin-bottom: 0.75rem;
}

.event-detail-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.6875rem;
  margin-bottom: 0.4rem;
  line-height: 1.3;

  i {
    font-size: 0.8125rem;
    width: 12px;
    flex-shrink: 0;
  }
}

.event-btn {
  width: 100%;
  background: white;
  color: #213ce5;
  border: none;
  border-radius: 0 !important;
  padding: 0.55rem;
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
}

.event-banner-collapsed {
  padding: 0.875rem 0;
  display: flex;
  justify-content: center;
  margin-top: auto;
  flex-shrink: 0;
}

.event-icon-collapsed {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }
}

// Form styling
.contact-title {
  padding: 0.5rem;
  p {
    font-weight: 500;
    font-size: 0.875rem;
    font-family: "GilroyMedium", sans-serif;
    color: #000;
    margin: 0;
    line-height: 1.2;
  }
}

.user-details,
.form-section {
  padding: 0.5rem;
  h6 { color: #000; }
}

.details-table {
  width: 100%;
  font-size: 0.875rem;
  color: #000;
}

.form-group {
  margin-bottom: 1rem;
  .form-control{
    padding: 0.5rem;
  }
}

// Phone row
.phone-group {
  display: flex;
  width: 100%;

  .phone-select {
    width: 50%;
    flex-shrink: 0;
  }

  .phone-input {
    width: 50%;
    padding: 8px 12px;
    font-size: 14px;
    color: #020202;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-left: 1px solid transparent;;
    outline: none;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &::placeholder {
      color: #718096;
    }

    &:focus {
      background-color: #fff;
      border-color: #213ce5;
      box-shadow: 0 0 0 2px rgba(33, 60, 229, 0.2);
    }
  }
}

// NSelect styling
:deep(.phone-select) {
  .n-base-selection {
    height: 100%;
    border: 1px solid #ced4da !important;
    border-radius: 0 !important;
    font-size: 14px;
    font-family: "Open Sans", sans-serif;
    background-color: #fff;
  }

  .n-base-selection-label {
    height: 100%;
    color: #020202;
    background-color: #fff;
  }

  .n-base-selection-placeholder__inner {
    color: #718096;
  }
  .n-base-selection-placeholder {
    color: #718096;
  }
}


// Textarea
.message-form {
  background-color: #fff !important;
  border-radius: 0 !important;
  border: 1px solid #ced4da;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  outline: none;

  &::placeholder { color: #718096; }

  &:focus {
    background-color: #fff !important;
    border-color: #213ce5 !important;
    box-shadow: 0 0 0 2px rgba(33, 60, 229, 0.2) !important;
  }
}

.form-actions {
  padding: 0.5rem;

  .btn-primary {
    background-color: #213ce5;
    border-color: #213ce5;
    border-radius: 0 !important;
    cursor: pointer;

    &:hover {
      background-color: #1e35cc;
      border-color: #1e35cc;
    }
  }
}

.required {
  font-size: 0.875rem;
  color: #718096;
}

:deep(.n-drawer) {
  border-radius: 0 !important;
  padding: 0 !important;
  background-color: #f8f9fa;
  max-width: 100vw;
}

:deep(.n-drawer-content) {
  border-radius: 0 !important;
  padding: 0 !important;
}

:deep(.n-drawer-body-content-wrapper) {
  border-radius: 0 !important;
  padding: 0 !important;
}

:deep(.card) {
  border-radius: 0 !important;
  .card-header { padding: 0.5rem; }
}

* { border-radius: 0 !important; }
</style>